import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateProfile,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

interface UserData {
  displayName: string;
  username: string;
  email: string;
  phoneNumber: string;
  createdAt: any;
}

const AccountDetailsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Edit account details modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  // password required for delete (reauth)
  const [deletePassword, setDeletePassword] = useState('');

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data() as UserData;
          setUserData(data);
        } else {
          setError('User data not found.');
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load user data.');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Open edit modal
  const handleOpenEditModal = () => {
    setEditUsername(userData?.username || '');
    setShowEditModal(true);
    setEditError('');
    setError('');
    setSuccess('');
  };

  // Check if username is available in usernameLookup
  const isUsernameAvailable = async (
    username: string,
    currentUserId: string
  ): Promise<boolean> => {
    try {
      const normalizedUsername = username.trim().toLowerCase();

      // Check in usernameLookup collection
      const lookupDoc = await getDoc(
        doc(db, 'usernameLookup', normalizedUsername)
      );

      if (!lookupDoc.exists()) {
        return true; // Username is available
      }

      // Username exists, check if it belongs to current user
      const lookupData = lookupDoc.data();
      return lookupData.uid === currentUserId;
    } catch (err) {
      console.error('Error checking username availability:', err);
      throw new Error('Failed to check username availability');
    }
  };

  // Update usernameLookup collection
  const updateUsernameLookup = async (
    oldUsername: string | null,
    newUsername: string,
    userId: string
  ) => {
    const normalizedNewUsername = newUsername.trim().toLowerCase();

    // Remove old username from lookup if it exists and is different
    if (
      oldUsername &&
      oldUsername.trim().toLowerCase() !== normalizedNewUsername
    ) {
      const normalizedOldUsername = oldUsername.trim().toLowerCase();
      try {
        await deleteDoc(doc(db, 'usernameLookup', normalizedOldUsername));
      } catch (err) {
        console.error('Error removing old username from lookup:', err);
        // Continue anyway, as the old username might not exist in lookup
      }
    }

    // Add new username to lookup
    await setDoc(doc(db, 'usernameLookup', normalizedNewUsername), {
      uid: userId,
      username: newUsername.trim(), // Changed from displayName to username
      updatedAt: new Date().toISOString(),
    });
  };

  // Save edited username
  const handleSaveEdit = async () => {
    if (!user || !editUsername.trim()) {
      setEditError('Username cannot be empty.');
      return;
    }

    // Check if username is the same as current
    if (editUsername.trim() === userData?.username) {
      setEditError('This is already your current username.');
      return;
    }

    // Validate username format (optional - add your own rules)
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(editUsername.trim())) {
      setEditError(
        'Username must be 3-20 characters and contain only letters, numbers, and underscores.'
      );
      return;
    }

    setSavingEdit(true);
    setEditError('');

    try {
      // Check if username is available
      const isAvailable = await isUsernameAvailable(
        editUsername.trim(),
        user.uid
      );

      if (!isAvailable) {
        setEditError(
          'This username is already taken. Please choose another one.'
        );
        setSavingEdit(false);
        return;
      }

      // Update Firestore document with username field (NOT displayName)
      await updateDoc(doc(db, 'users', user.uid), {
        username: editUsername.trim(),
      });

      // Update usernameLookup collection
      await updateUsernameLookup(
        userData?.username || null,
        editUsername.trim(),
        user.uid
      );

      // Update local state
      setUserData((prev) =>
        prev ? { ...prev, username: editUsername.trim() } : null
      );
      setShowEditModal(false);
      setSuccess('Account details updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating account details:', err);
      if (err.message === 'Failed to check username availability') {
        setEditError(err.message);
      } else {
        setEditError('Failed to update account details. Please try again.');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // delete with re-auth first
  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeleting(true);
    setError('');

    try {
      if (!user.email) {
        setError('Missing user email. Please log out and log in again.');
        setDeleting(false);
        return;
      }

      if (!deletePassword.trim()) {
        setError('Please enter your current password to confirm deletion.');
        setDeleting(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        deletePassword
      );
      await reauthenticateWithCredential(user, credential);

      // Delete user document
      await deleteDoc(doc(db, 'users', user.uid));

      // Delete username from usernameLookup
      if (userData?.username) {
        const normalizedUsername = userData.username.trim().toLowerCase();
        try {
          await deleteDoc(doc(db, 'usernameLookup', normalizedUsername));
        } catch (err) {
          console.error('Error deleting username lookup:', err);
          // Continue with account deletion anyway
        }
      }

      // Delete the Firebase Auth user
      await deleteUser(user);

      setShowDeleteModal(false);
      setDeletePassword('');

      navigate('/', {
        state: { message: 'Your account has been deleted successfully' },
      });
    } catch (err: any) {
      console.error('Error deleting account:', err);

      if (
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential'
      ) {
        setError('Current password is incorrect.');
      } else if (err.code === 'auth/requires-recent-login') {
        setError(
          'For security reasons, please log out and log back in, then try deleting again.'
        );
      } else {
        setError('Failed to delete account. Please contact support.');
      }

      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Account Details</h2>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {error && !showDeleteModal && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Top Card: Account Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-gray-900">
            Account Details
          </h3>
          <button
            onClick={handleOpenEditModal}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            Edit Account Details
          </button>
        </div>

        <div className="grid grid-cols-3 gap-8">
          {/* Username - Read only */}
          <div>
            <p className="text-xs text-gray-500">Username</p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {userData?.username || '-'}
            </p>
          </div>

          {/* Email - Read only */}
          <div>
            <p className="text-xs text-gray-500">Email</p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {userData?.email || '-'}
            </p>
          </div>

          {/* Phone - Read only */}
          <div>
            <p className="text-xs text-gray-500">Phone Number</p>
            <p className="mt-2 text-sm font-medium text-gray-900">
              {userData?.phoneNumber || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          Delete Account
        </h3>

        <p className="text-sm text-gray-600 mb-6">
          Once your account is deleted your subscription will also be cancelled.
        </p>

        <button
          onClick={() => {
            setShowDeleteModal(true);
            setError('');
            setSuccess('');
          }}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Delete Account
        </button>
      </div>

      {/* Edit Account Details Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Edit Account Details
            </h3>

            {editError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{editError}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              {/* Username - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={savingEdit}
                  placeholder="Enter username"
                />
                <p className="text-xs text-gray-500 mt-1">
                  3-20 characters, letters, numbers, and underscores only
                </p>
              </div>

              {/* Email - Greyed out */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userData?.email || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* Phone Number - Greyed out */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={userData?.phoneNumber || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  disabled
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditError('');
                }}
                disabled={savingEdit}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Delete Account?
            </h3>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your account? This action cannot
              be undone and your subscription will be cancelled.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current password (required)
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                disabled={deleting}
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setError('');
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetailsPage;
